import { DateTime } from 'luxon';
import { compareSemver, trimAppVersion } from '@cf-shared/version-compare';
import { describeError } from '@cf-shared/error-describe';
import { VersionCheckResult } from './version-check.dispatch';

// Pure, dependency-injected core of the iOS in-app-update detector.
// Apple has no in-app update API, so we query the public iTunes Lookup
// endpoint and compare its `version` field to the running build. This
// module holds the semver / region / response-parse helpers AND the
// cache+fetch orchestration, all free of Angular and Capacitor so the
// whole decision path is unit-testable in plain Node — the same split
// the Android backend uses (mapAndroidPluginInfo lives in
// version-check.dispatch.ts). The thin Angular wrapper that supplies the
// real fetch / CapApp.getInfo / clock lives in version-check.ios.ts.
//
// See design-docs/in-app-update-prompt.md → iOS — iTunes Lookup.

// Only the public prod bundle has an App Store listing. The dev bundle
// (`app.mhphysique.fitness.dev`) and any sideload are gated out — they
// get their updates through TestFlight, which manages itself. Matches
// the `appId` in client/capacitor.config.ts.
export const IOS_PROD_BUNDLE_ID = 'app.mhphysique.fitness';

// 6h in-process cache so we don't hit Apple's Lookup API on every
// dashboard render. Apple's own edge cache adds 1-24h propagation lag,
// so a tighter TTL would buy nothing.
export const LOOKUP_CACHE_TTL_MS = 6 * 60 * 60 * 1000;

// Network timeout for the single Lookup request. Deliberately generous:
// this is a non-urgent, non-blocking background check (the "update
// available" nudge), so it can afford to wait. A short budget turned every
// slow moment on a flaky mobile connection into a reported "failure"
// (Sentry VersionCheckIosLookupFailed / issue 451) even though the lookup
// would have answered if given a little longer. We would rather wait and
// get the real answer — and if the lookup genuinely cannot complete within
// 30 s, that is a real signal worth reporting, not network noise. The 6h
// cache and once-per-session-per-surface throttle mean this long ceiling is
// paid at most once in a while, never on a hot path.
export const LOOKUP_TIMEOUT_MS = 30 * 1000;

// How far past the timeout a failed lookup may end before we conclude the app
// was frozen while it waited. A timer that fires on time ends the lookup a few
// milliseconds after LOOKUP_TIMEOUT_MS; one that ends much later could only have
// been held up by iOS suspending the app (Sentry issue 451: a timer that fired
// about 11 minutes late, the moment the user came back).
export const LOOKUP_FROZEN_SLACK_MS = 5 * 1000;

// The part of `document` that watchPageHidden reads, so tests can pass a fake.
export interface VisibilitySource {
  visibilityState: string;
  addEventListener(type: 'visibilitychange', listener: () => void): void;
  removeEventListener(type: 'visibilitychange', listener: () => void): void;
}

// Starts watching whether the page is hidden. The returned function stops
// watching and answers whether the page was hidden when the watch began or
// became hidden at any moment since; the same signal the Firebase auth wait
// uses (couch-login-firebase-ready.ts, hiddenDuringWait). With no page at all
// (not a browser) it always answers false.
export function watchPageHidden(page: VisibilitySource | undefined): () => boolean {
  if (!page) return () => false;
  let wasHidden = page.visibilityState === 'hidden';
  const listener = () => {
    if (page.visibilityState === 'hidden') wasHidden = true;
  };
  page.addEventListener('visibilitychange', listener);
  return () => {
    page.removeEventListener('visibilitychange', listener);
    return wasHidden;
  };
}

// Storefront region used when navigator.language gives us nothing usable.
export const DEFAULT_REGION = 'us';

// `trimAppVersion` and `compareSemver` live in `@cf-shared/version-compare` —
// the ops CLI under `tools/` needs them too, and `tools/` can read `shared/src`
// but not `client/`. Re-exported here so existing callers are unchanged.
export { compareSemver, trimAppVersion };

// True iff the App Store version is strictly newer than the running
// build. The App Store version is already clean (`1.24.2`); the running
// version still carries the `v` prefix and `-<sha>` suffix, so trim it.
export function isAppStoreNewer(appStoreVersion: string, runningVersion: string): boolean {
  return compareSemver(appStoreVersion, trimAppVersion(runningVersion)) > 0;
}

// Derives the iTunes storefront region from a BCP-47 language tag like
// `en-US`. Takes the LAST `-`-separated segment as the region candidate
// (so script-subtag tags like `zh-Hans-CN` still resolve to `cn`),
// lowercases it, and accepts it only if it looks like a 2-letter region
// code. Anything else (no region subtag, `C`, empty, undefined) falls
// back to `us`.
//
// NOTE: on WKWebView navigator.language is the iOS UI locale, not
// necessarily the App Store storefront. We treat it as a heuristic; the
// `country=` parameter mostly affects propagation timing of the
// `version` field, not correctness — see the design doc.
export function regionFromLanguage(lang: string | undefined | null): string {
  if (!lang) return DEFAULT_REGION;
  const segments = lang.split('-');
  if (segments.length < 2) return DEFAULT_REGION;
  const region = segments[segments.length - 1].toLowerCase();
  return /^[a-z]{2}$/.test(region) ? region : DEFAULT_REGION;
}

// Builds the iTunes Lookup URL for a bundle id + storefront region.
export function buildLookupUrl(bundleId: string, region: string): string {
  return `https://itunes.apple.com/lookup?bundleId=${bundleId}&country=${region}`;
}

// Outcome of parsing an iTunes Lookup response body. `no-results` is a
// legitimate empty storefront (the release hasn't propagated to that
// region yet) — a normal "no update" we cache. `malformed` is a protocol
// failure (body isn't an object, `results` isn't an array, or the first
// result has no usable `version`) — the caller throws on it so it is
// logged and NOT cached.
export type LookupParse =
  | { kind: 'version'; version: string }
  | { kind: 'no-results' }
  | { kind: 'malformed'; reason: string };

export function parseLookupResponse(body: unknown): LookupParse {
  if (body === null || typeof body !== 'object') {
    return { kind: 'malformed', reason: 'body-not-object' };
  }
  const results = (body as { results?: unknown }).results;
  if (!Array.isArray(results)) {
    return { kind: 'malformed', reason: 'results-not-array' };
  }
  if (results.length === 0) {
    return { kind: 'no-results' };
  }
  const version = (results[0] as { version?: unknown })?.version;
  if (typeof version !== 'string' || version.length === 0) {
    return { kind: 'malformed', reason: 'missing-version' };
  }
  return { kind: 'version', version };
}

// Runtime inputs the orchestrator needs, injected so the cache / gate /
// fetch path is testable with plain fakes. The Angular wrapper supplies
// the real implementations (fetch, CapApp.getInfo, navigator.language,
// AppVersion, DateTime.now, LoggingService.trackSilentProblem).
export interface IosLookupDeps {
  getBundleId: () => Promise<string>;
  getRunningVersion: () => string;
  getLanguage: () => string | undefined;
  // Resolves with the parsed JSON body, or rejects on timeout / non-200 /
  // network error / JSON-parse failure. All rejections are treated as a
  // lookup failure (no update, plus a silent problem unless the device is
  // offline — see isOfflineAsync).
  fetchJson: (url: string, timeoutMs: number) => Promise<unknown>;
  now: () => DateTime;
  trackProblem: (name: string, context: { code?: string; message?: string; offlineCheckError?: string }) => void;
  // Asks whether the device is really offline right now (a fresh reachability
  // check, not a cached belief). Resolves true only when it is offline. A lookup
  // failure while offline is expected, so it is logged and not reported (#6087).
  isOfflineAsync: () => Promise<boolean>;
  // Starts watching whether the app is hidden (in the background). Returns a
  // function that stops watching and answers whether the app was hidden when the
  // watch began or at any moment since. iOS suspends a hidden app, so a lookup
  // that ran while hidden says nothing about the lookup itself.
  watchHidden: () => () => boolean;
}

// The single-slot cache. Only ever one Lookup call shape per session
// (bundle id + region are fixed for the running build), so a single slot
// suffices — no region partitioning.
type CacheSlot =
  | { state: 'empty' }
  | { state: 'inflight'; promise: Promise<VersionCheckResult> }
  | { state: 'resolved'; result: VersionCheckResult; at: DateTime };

export class IosUpdateLookup {
  private slot: CacheSlot = { state: 'empty' };

  constructor(private deps: IosLookupDeps) {}

  getStatusAsync(): Promise<VersionCheckResult> {
    // a fetch is already running — dedup concurrent callers onto it so
    // only one HTTP request fires for the whole burst.
    if (this.slot.state === 'inflight') {
      return this.slot.promise;
    }
    // a fresh-enough successful result is on hand — serve it, no network.
    if (this.slot.state === 'resolved') {
      const ageMs = this.deps.now().diff(this.slot.at).as('milliseconds');
      if (ageMs < LOOKUP_CACHE_TTL_MS) {
        return Promise.resolve(this.slot.result);
      }
    }
    // empty, or resolved-but-stale: start a fresh lookup. flip the slot to
    // inflight BEFORE the first await so any late concurrent caller dedups
    // onto this new promise (PRD §3 case 3).
    const promise = this.runLookupAsync();
    this.slot = { state: 'inflight', promise };
    return promise;
  }

  private async runLookupAsync(): Promise<VersionCheckResult> {
    const startedAt = this.deps.now();
    const stopWatchingHidden = this.deps.watchHidden();
    try {
      const result = await this.performLookupAsync();
      stopWatchingHidden();
      // cache only successful resolutions — that includes the bundle-id
      // gate and a legit resultCount:0 "no update".
      this.slot = { state: 'resolved', result, at: this.deps.now() };
      return result;
    } catch (err) {
      const code = (err as Error)?.name;
      const message = (err as Error)?.message;
      const wasHidden = stopWatchingHidden();
      const elapsedMs = this.deps.now().diff(startedAt).as('milliseconds');
      const wasFrozen = elapsedMs > LOOKUP_TIMEOUT_MS + LOOKUP_FROZEN_SLACK_MS;
      // if the app was in the background or frozen while it waited, iOS suspended
      // it and the failure says nothing about the lookup, so log it and do not
      // report it (Sentry issue 451). if the device is offline, the failure is
      // expected for the same reason (#6087). otherwise surface it as a silent
      // problem so chronic Lookup breakage is visible without a Sentry storm.
      if (wasHidden || wasFrozen) {
        console.log(`VersionCheck iOS: iTunes Lookup failed while the app was ${wasHidden ? 'in the background' : 'frozen'} (${Math.round(elapsedMs)}ms), not reporting (${code}: ${message})`);
        this.slot = { state: 'empty' };
        return { updateAvailable: false };
      }
      const offline = await this.checkOfflineAsync();
      if (offline.isOffline) {
        console.log(`VersionCheck iOS: iTunes Lookup failed while offline, not reporting (${code}: ${message})`);
      } else {
        this.deps.trackProblem('VersionCheckIosLookupFailed', {
          code,
          message,
          ...(offline.checkError !== undefined ? { offlineCheckError: offline.checkError } : {}),
        });
      }
      // clear the slot back to empty BEFORE the awaiting callers receive
      // their answer, so a failure never populates the cache and the next
      // call retries (PRD §3/§5). it is cleared only after the offline check,
      // so a caller arriving during that check still joins this lookup.
      this.slot = { state: 'empty' };
      return { updateAvailable: false };
    }
  }

  // asks the injected offline check. a failure of the check itself counts as
  // "not known to be offline", so the lookup failure is still reported — a broken
  // check must never hide real Lookup breakage — and the check's own error rides
  // along in that report so it reaches Sentry too.
  private async checkOfflineAsync(): Promise<{ isOffline: boolean; checkError?: string }> {
    try {
      return { isOffline: await this.deps.isOfflineAsync() };
    } catch (checkErr) {
      return { isOffline: false, checkError: describeError(checkErr) };
    }
  }

  private async performLookupAsync(): Promise<VersionCheckResult> {
    const bundleId = await this.deps.getBundleId();
    if (bundleId !== IOS_PROD_BUNDLE_ID) {
      // not the public prod bundle (e.g. `.dev` or sideload) — skip the
      // network call entirely; TestFlight handles dev updates itself.
      return { updateAvailable: false };
    }

    const region = regionFromLanguage(this.deps.getLanguage());
    const url = buildLookupUrl(IOS_PROD_BUNDLE_ID, region);
    const body = await this.deps.fetchJson(url, LOOKUP_TIMEOUT_MS);

    const parsed = parseLookupResponse(body);
    if (parsed.kind === 'malformed') {
      // a malformed payload is a protocol failure: throw so runLookupAsync
      // logs it and does NOT cache. (resultCount:0 is handled below and is
      // NOT a failure.)
      throw new Error(`malformed iTunes Lookup response: ${parsed.reason}`);
    }
    if (parsed.kind === 'no-results') {
      // the storefront for this region doesn't list the app yet — a normal
      // outcome we treat as "no update" and cache.
      return { updateAvailable: false };
    }
    if (isAppStoreNewer(parsed.version, this.deps.getRunningVersion())) {
      return { updateAvailable: true, availableVersion: parsed.version };
    }
    return { updateAvailable: false };
  }
}
